import { useEditorJS } from "@/editor/hooks/useEditorJs";
import { getCustomEditorTools } from "@/editor/utils/editorjs-tools";
import { baseURL } from "@/services/index.services";
import Cookies from "js-cookie";
import React, { useEffect, useRef, useState, useCallback } from "react";
import "@/editor/utils/editorjs-style.scss";
import { useJsonEditor } from "@/editor/hooks/useJsonEditor";
import CustomJsonEditor from "@/editor/components/CustomJsonEditor";
import { OutputData } from "@editorjs/editorjs";
import LabelSwitch from "@ui/external/components/LabelSwitch";

interface EditorJSProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
}

const EditorJSComponent: React.FC<EditorJSProps> = ({
  value,
  onChange,
  placeholder,
  height = 300,
}) => {
  const isDataLoaded = useRef(false);
  const [jsonEditMode, setJsonEditMode] = useState(false);
  const {
    editorData,
    setEditorData,
    searchQuery,
    setSearchQuery,
    historyConfig,
  } = useJsonEditor();

  const imageUploadFn = async (file: File) => {
    const formData = new FormData();

    // Pasted files often don't have a name, which backend might require.
    // We create a new File object with a timestamped name if necessary.
    const fileName =
      file.name && file.name !== "image.png"
        ? file.name
        : `pasted-${Date.now()}.${file.type.split("/")[1] || "png"}`;

    const finalFile = new File([file], fileName, { type: file.type });
    formData.append("image", finalFile);

    const response = await fetch(`${baseURL}/custom-gallery/`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${
          sessionStorage.getItem("access") || Cookies.get("access")
        }`,
      },
    });
    const result = await response.json();
    if (response.ok) {
      return { url: result.image };
    } else {
      throw new Error(result.error?.message || "Image upload failed");
    }
  };

  // Memoize tools to prevent re-initialization
  const tools = React.useMemo(
    () =>
      getCustomEditorTools(imageUploadFn, {
        faqEnabled: true,
        accordianEnabled: true,
      }),
    [],
  );

  // Memoize initialData so it doesn't change on every keystroke
  const initialData = React.useMemo(() => {
    if (value && !isDataLoaded.current) {
      try {
        const parsed = JSON.parse(value);
        isDataLoaded.current = true;
        setEditorData(parsed);
        return parsed;
      } catch (e) {
        console.warn("Failed to parse Editor.js data", e);
      }
    }
    return undefined;
  }, []); // Only parse once on mount

  const lastVisualUpdateRef = useRef("");

  const { holderRef, isReady, render, save, editor } = useEditorJS({
    placeholder,
    minHeight: height,
    tools: tools as any,
    data: initialData,
    onChange: (data) => {
      const dataStr = JSON.stringify(data);
      lastVisualUpdateRef.current = dataStr;
      onChange(dataStr);
      setEditorData(data);
    },
  });

  // Handle data fetching for existing blogs (Initial Load)
  useEffect(() => {
    if (isReady && value && !isDataLoaded.current) {
      // If we already have content in 'lastVisualUpdateRef', we've likely started
      // typing or just initialized, so we don't want to force a render.
      if (lastVisualUpdateRef.current) {
        isDataLoaded.current = true;
        return;
      }

      try {
        const parsed = JSON.parse(value);
        if (parsed && (parsed.blocks || parsed.time)) {
          render(parsed);
          setEditorData(parsed);
          isDataLoaded.current = true;
          lastVisualUpdateRef.current = value;
        }
      } catch (e) {
        // Not valid JSON or corrupted data, skip initial render
      }
    }
  }, [value, isReady, render, setEditorData]);

  // Sync from JSON editor back to Editor.js visual editor
  useEffect(() => {
    if (jsonEditMode === false && editorData.present && editor && isReady) {
      const newDataStr = JSON.stringify(editorData.present);

      // Only render if the change DID NOT come from the visual editor itself
      if (newDataStr !== lastVisualUpdateRef.current) {
        editor.render(editorData.present as OutputData);
        lastVisualUpdateRef.current = newDataStr;
      }
    }
  }, [jsonEditMode, editorData.present, editor, isReady]);

  const handleToggleMode = useCallback(async () => {
    if (!jsonEditMode) {
      // Switching TO JSON mode: capture current visual editor state
      const currentData = await save();
      if (currentData) {
        setEditorData(currentData);
      }
    }
    setJsonEditMode(!jsonEditMode);
  }, [jsonEditMode, save, setEditorData]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center bg-[linear-gradient(180deg,_#1DAA7E_0%,_#1A8463_100%)] text-white px-4 py-2 rounded-t-md">
        <span className="text-sm font-semibold">Visual Editor</span>
        <div className="flex items-center space-x-2 text-sm">
          <span>Edit JSON</span>
          <LabelSwitch
            label=""
            checked={jsonEditMode}
            onClick={handleToggleMode}
          />
        </div>
      </div>

      <div className="border border-t-0 rounded-b-md bg-white min-h-[400px]">
        <div className={jsonEditMode ? "block" : "hidden"}>
          <CustomJsonEditor
            data={editorData.present || {}}
            setEditorData={setEditorData}
            setSearchQuery={setSearchQuery}
            searchText={searchQuery}
            rootName="Blog Content"
            historyConfig={historyConfig}
            className="!rounded-none border-0"
          />
        </div>
        <div
          className={`${!jsonEditMode ? "block" : "hidden"} p-4 editor-style`}
        >
          <div ref={holderRef} className="prose max-w-none" />
        </div>
      </div>
    </div>
  );
};

export default EditorJSComponent;
